import { useState } from 'react'
import { motion } from 'framer-motion'
import { Plus, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { cn } from '../lib/utils'

// ─── Types ────────────────────────────────────────────────────────────────────

type WOStage = 'New' | 'Assigned' | 'In Progress' | 'Review' | 'Complete'
type WOPriority = 'Urgent' | 'Normal' | 'Low'

type WorkOrder = {
  id: string; title: string; unit: string; property: string
  priority: WOPriority; assignee: string; dueDate: string
  stage: WOStage; cost: number; description: string; reported: string
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const INITIAL_ORDERS: WorkOrder[] = [
  { id: 'wo1', title: 'HVAC Not Cooling – Unit 4B', unit: '4B', property: 'Maple Grove', priority: 'Urgent', assignee: 'Northstar HVAC', dueDate: '2026-07-09', stage: 'In Progress', cost: 650, description: 'Tenant reports AC stopped working on Jul 6. Compressor failure suspected.', reported: '2026-07-06' },
  { id: 'wo2', title: 'Leak Under Sink – Unit 12C', unit: '12C', property: 'Willow Creek', priority: 'Normal', assignee: 'Rapid Plumbing', dueDate: '2026-07-12', stage: 'Assigned', cost: 420, description: 'Slow drip under kitchen sink. Tenant notified water damage risk.', reported: '2026-07-07' },
  { id: 'wo3', title: 'Paint Touch-Up – Unit 8', unit: 'Unit 8', property: 'Cedar Ridge', priority: 'Low', assignee: 'Urban Finish', dueDate: '2026-07-20', stage: 'New', cost: 280, description: 'Scuff marks on living room wall. Pre-move-out touch-up.', reported: '2026-07-05' },
  { id: 'wo4', title: 'Garbage Disposal Replace – 4B', unit: '4B', property: 'Maple Grove', priority: 'Normal', assignee: 'Unassigned', dueDate: '2026-07-15', stage: 'New', cost: 190, description: 'Disposal unit stopped working. Needs replacement.', reported: '2026-07-07' },
  { id: 'wo5', title: 'Exterior Light Repair – Cedar', unit: 'Exterior', property: 'Cedar Ridge', priority: 'Low', assignee: 'CityElectric', dueDate: '2026-07-18', stage: 'Assigned', cost: 95, description: 'Front porch light fixture flickering. Wiring issue.', reported: '2026-07-04' },
  { id: 'wo6', title: 'Window Seal Replacement – 12C', unit: '12C', property: 'Willow Creek', priority: 'Normal', assignee: 'GlassPro', dueDate: '2026-07-10', stage: 'Review', cost: 340, description: 'Bedroom window seal broken. Air leaking in.', reported: '2026-06-30' },
  { id: 'wo7', title: 'Deep Clean Turnover – 2A', unit: '2A', property: 'Maple Grove', priority: 'Urgent', assignee: 'CleanTeam Pro', dueDate: '2026-07-08', stage: 'Complete', cost: 350, description: 'Unit 2A prep for new tenant. Full deep clean required.', reported: '2026-07-01' },
]

const ALL_STAGES: WOStage[] = ['New', 'Assigned', 'In Progress', 'Review', 'Complete']

const STAGE_COLOR: Record<WOStage, string> = {
  New: 'text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-400',
  Assigned: 'text-blue-700 bg-blue-50 dark:bg-blue-950/30 dark:text-blue-300',
  'In Progress': 'text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300',
  Review: 'text-violet-700 bg-violet-50 dark:bg-violet-950/30 dark:text-violet-300',
  Complete: 'text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300',
}

const PRIORITY_STYLE: Record<WOPriority, string> = {
  Urgent: 'text-rose-700 bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-800',
  Normal: 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  Low: 'text-slate-600 bg-slate-100 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-600',
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n)

// ─── Board View ───────────────────────────────────────────────────────────────

function BoardView({ orders, onMove }: { orders: WorkOrder[]; onMove: (id: string, dir: 1 | -1) => void }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      {ALL_STAGES.map(stage => {
        const stageOrders = orders.filter(o => o.stage === stage)
        return (
          <div key={stage} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col">
            <div className={cn('flex items-center justify-between px-3 py-2.5 border-b border-slate-200 dark:border-slate-700', STAGE_COLOR[stage])}>
              <span className="text-xs font-bold uppercase tracking-wide">{stage}</span>
              <span className="rounded-full bg-white/60 dark:bg-black/20 px-2 py-0.5 text-[10px] font-bold">{stageOrders.length}</span>
            </div>
            <div className="p-2 space-y-2 flex-1 min-h-[150px] bg-slate-50 dark:bg-slate-800/40">
              {stageOrders.map(wo => (
                <motion.div key={wo.id} layout
                  className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-bold text-slate-900 dark:text-slate-100 leading-tight">{wo.title}</p>
                    <span className={cn('shrink-0 rounded-full border px-1.5 py-0.5 text-[9px] font-bold', PRIORITY_STYLE[wo.priority])}>{wo.priority}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">{wo.unit} · {wo.property}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate">{wo.assignee === 'Unassigned' ? <span className="text-rose-500">⚠ Unassigned</span> : wo.assignee}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-[10px] text-slate-500">Due {wo.dueDate.slice(5)}</span>
                    <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300">{fmt(wo.cost)}</span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    {ALL_STAGES.indexOf(stage) > 0 && (
                      <Button size="sm" variant="secondary" className="h-6 px-1.5 text-[10px] flex-1" onClick={() => onMove(wo.id, -1)}>←</Button>
                    )}
                    {ALL_STAGES.indexOf(stage) < ALL_STAGES.length - 1 && (
                      <Button size="sm" className="h-6 px-1.5 text-[10px] flex-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => onMove(wo.id, 1)}>→</Button>
                    )}
                  </div>
                </motion.div>
              ))}
              {stageOrders.length === 0 && (
                <div className="flex items-center justify-center h-20 text-[11px] text-slate-400 dark:text-slate-600">Empty</div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── List View ────────────────────────────────────────────────────────────────

function ListView({ orders, onMove }: { orders: WorkOrder[]; onMove: (id: string) => void }) {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/60">
                {['Work Order', 'Priority', 'Property', 'Assignee', 'Due', 'Cost', 'Stage', ''].map(h => (
                  <th key={h} className="px-4 py-2.5 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200 dark:border-slate-700">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map(wo => (
                <tr key={wo.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm">{wo.title}</p>
                    <p className="text-xs text-slate-500">{wo.unit} · Reported {wo.reported.slice(5)}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full border px-2 py-0.5 text-[11px] font-semibold', PRIORITY_STYLE[wo.priority])}>{wo.priority}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{wo.property}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{wo.assignee === 'Unassigned' ? <span className="text-rose-500">⚠ {wo.assignee}</span> : wo.assignee}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{wo.dueDate}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{fmt(wo.cost)}</td>
                  <td className="px-4 py-3">
                    <span className={cn('rounded-full px-2.5 py-0.5 text-[11px] font-semibold', STAGE_COLOR[wo.stage])}>{wo.stage}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button className="text-slate-400 hover:text-violet-600 transition-colors" onClick={() => onMove(wo.id)}>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Main Export ──────────────────────────────────────────────────────────────

export default function KanbanModule() {
  const [orders, setOrders] = useState<WorkOrder[]>(INITIAL_ORDERS)
  const [view, setView] = useState<'board' | 'list'>('board')
  const [filter, setFilter] = useState<WOPriority | 'All'>('All')
  const [notice, setNotice] = useState<string | null>(null)

  const moveOrder = (id: string, dir: 1 | -1) => {
    setOrders(prev => prev.map(o => {
      if (o.id !== id) return o
      const idx = ALL_STAGES.indexOf(o.stage)
      const next = ALL_STAGES[Math.max(0, Math.min(ALL_STAGES.length - 1, idx + dir))]
      return { ...o, stage: next }
    }))
  }

  const createWorkOrder = () => {
    const created = new Date().toISOString().slice(0, 10)
    const due = new Date(Date.now() + 4 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    const nextOrder: WorkOrder = {
      id: `wo${Date.now()}`,
      title: 'New Work Order Request',
      unit: 'TBD',
      property: 'Maple Grove',
      priority: 'Normal',
      assignee: 'Unassigned',
      dueDate: due,
      stage: 'New',
      cost: 0,
      description: 'Created from board action.',
      reported: created,
    }
    setOrders((prev) => [nextOrder, ...prev])
    setView('list')
    setNotice('New work order added and moved to list view for assignment.')
  }

  const displayed = filter === 'All' ? orders : orders.filter(o => o.priority === filter)

  const urgentCount = orders.filter(o => o.priority === 'Urgent' && o.stage !== 'Complete').length
  const unassigned = orders.filter(o => o.assignee === 'Unassigned').length
  const totalCost = orders.filter(o => o.stage !== 'Complete').reduce((s, o) => s + o.cost, 0)
  const doneCount = orders.filter(o => o.stage === 'Complete').length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-900 dark:text-slate-100">Work Order Board</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">Visual maintenance workflow tracker</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={createWorkOrder}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />New Work Order
          </Button>
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {(['board', 'list'] as const).map(v => (
              <button key={v} onClick={() => setView(v)}
                className={cn('px-3 py-1.5 text-xs font-medium transition-colors capitalize',
                  view === v ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                )}>{v}</button>
            ))}
          </div>
        </div>
      </div>

      {notice && (
        <Card className="p-3 shadow-sm border-emerald-200 bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/25">
          <p className="text-xs text-emerald-800 dark:text-emerald-300">{notice}</p>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Urgent Open</p>
          <p className="mt-1 text-2xl font-bold text-rose-700 dark:text-rose-400">{urgentCount}</p>
          {urgentCount > 0 && <p className="text-xs text-rose-600 mt-1">Needs immediate attention</p>}
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Unassigned</p>
          <p className="mt-1 text-2xl font-bold text-amber-700 dark:text-amber-400">{unassigned}</p>
          {unassigned > 0 && <p className="text-xs text-amber-600 mt-1">Needs vendor assignment</p>}
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Open Cost Est.</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-slate-100">{fmt(totalCost)}</p>
          <p className="text-xs text-slate-500 mt-1">Pending work orders</p>
        </Card>
        <Card className="p-3 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Completed</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-400">{doneCount}</p>
          <p className="text-xs text-emerald-600 mt-1">This month</p>
        </Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        <span className="text-xs text-slate-500 self-center font-medium">Filter:</span>
        {(['All', 'Urgent', 'Normal', 'Low'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filter === f
                ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 border-slate-900 dark:border-slate-100'
                : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            )}>{f}</button>
        ))}
        <span className="text-xs text-slate-400 self-center ml-auto">{displayed.length} orders</span>
      </div>

      {view === 'board' ? <BoardView orders={displayed} onMove={moveOrder} /> : <ListView orders={displayed} onMove={(id) => {
        moveOrder(id, 1)
        setNotice('Work order advanced to next stage.')
      }} />}
    </div>
  )
}
